import React, { useEffect, useState } from 'react'
import { Button } from '@components/common'
import { isImageFile, isPdfFile, renderPdfPages } from '@utils/pdf-preview.util'
import { getFaxFileSignedUrl } from '@services/api/fax/document.api'

type Props = {
  file?: File
  fileUrl?: string
  fileName: string
  mimeType?: string
  variant?: 'cards' | 'stacked'
  onRemove?: () => void
  onLoadStart?: () => void
  onLoadEnd?: () => void
  draggable?: boolean
  isDragging?: boolean
  isDragOver?: boolean
  onDragStart?: React.DragEventHandler<HTMLDivElement>
  onDragOver?: React.DragEventHandler<HTMLDivElement>
  onDrop?: React.DragEventHandler<HTMLDivElement>
  onDragEnd?: React.DragEventHandler<HTMLDivElement>
}

const FaxDocumentPagePreview: React.FC<Props> = ({
  file,
  fileUrl,
  fileName,
  mimeType,
  variant = 'cards',
  onRemove,
  onLoadStart,
  onLoadEnd,
  draggable = false,
  isDragging = false,
  isDragOver = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) => {
  const [pages, setPages] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    let ended = false
    let imageUrl: string | null = null
    setLoading(true)
    onLoadStart?.()

    const finish = (): void => {
      if (ended) return
      ended = true
      onLoadEnd?.()
    }

    const load = async (): Promise<void> => {
      try {
        if (file) {
          if (isPdfFile(mimeType, fileName)) {
            const rendered = await renderPdfPages(file)
            if (!cancelled) {
              setPages(rendered)
              setLoading(false)
            }
            return
          }
          if (isImageFile(mimeType)) {
            imageUrl = URL.createObjectURL(file)
            if (!cancelled) {
              setPages([imageUrl])
              setLoading(false)
            }
            return
          }
          if (!cancelled) {
            setPages([])
            setLoading(false)
          }
          return
        }

        if (fileUrl) {
          const signed = await getFaxFileSignedUrl(fileUrl)
          const url = signed.data?.url
          if (!url) {
            if (!cancelled) {
              setPages([])
              setLoading(false)
            }
            return
          }
          if (isPdfFile(mimeType, fileName)) {
            const rendered = await renderPdfPages(url)
            if (!cancelled) {
              setPages(rendered)
              setLoading(false)
            }
            return
          }
          if (!cancelled) {
            setPages([url])
            setLoading(false)
          }
          return
        }

        if (!cancelled) {
          setPages([])
          setLoading(false)
        }
      } finally {
        finish()
      }
    }

    void load()

    return () => {
      cancelled = true
      finish()
      if (imageUrl) URL.revokeObjectURL(imageUrl)
    }
  }, [file, fileUrl, fileName, mimeType, onLoadStart, onLoadEnd])

  if (variant === 'stacked') {
    if (loading) {
      return (
        <div className="fax-doc-pages">
          <div className="fax-doc-page fax-doc-page-loading" />
        </div>
      )
    }
    if (pages.length === 0) {
      return (
        <div className="fax-doc-pages">
          <div className="fax-doc-page fax-doc-page-fallback">
            <span className="mgc_file_fill" />
            <span className="fax-doc-page-name">{fileName}</span>
          </div>
        </div>
      )
    }
    return (
      <div className="fax-doc-pages">
        {pages.map((src, index) => (
          <div key={src.slice(0, 32) + index} className="fax-doc-page">
            <img src={src} alt={`${fileName} page ${index + 1}`} />
            <span className="fax-doc-page-number">Page {index + 1}</span>
          </div>
        ))}
      </div>
    )
  }

  const removeButton = onRemove ? (
    <Button
      className="button-secondary fax-doc-remove"
      onClick={onRemove}
      title="Remove document"
    >
      Remove
    </Button>
  ) : null

  const cardClassName = [
    'fax-doc-card',
    'fax-doc-page-card',
    draggable ? 'fax-doc-card-draggable' : '',
    isDragging ? 'is-dragging' : '',
    isDragOver ? 'is-drag-over' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const dragProps = draggable
    ? { draggable: true, onDragStart, onDragOver, onDrop, onDragEnd }
    : {}

  if (loading) {
    return (
      <div className={cardClassName} {...dragProps}>
        <span className="fax-doc-card-name">{fileName}</span>
        <div className="fax-doc-page-image fax-doc-page-loading" />
        {removeButton}
      </div>
    )
  }

  if (pages.length === 0) {
    return (
      <div className={cardClassName} {...dragProps}>
        <div className="fax-doc-page-image fax-doc-page-fallback">
          <span className="mgc_file_fill" />
        </div>
        <span className="fax-doc-card-name">{fileName}</span>
        {removeButton}
      </div>
    )
  }

  return (
    <div className={cardClassName} {...dragProps}>
      <span
        className="fax-doc-card-name"
        data-bs-toggle="tooltip"
        title={fileName}
      >
        {fileName}
      </span>
      <div className="fax-doc-page-image">
        <img src={pages[0]} alt={fileName} draggable={false} />
      </div>
      <span className="fax-doc-page-number"> {pages.length} pages</span>
      {onRemove ? (
        <button
          type="button"
          className="unstyled-button fax-doc-remove mgc_close_circle_fill"
          onClick={onRemove}
          title="Remove document"
          aria-label="Remove document"
        />
      ) : (
        <span
          className="fax-doc-remove mgc_close_circle_fill"
          title="Remove document"
        />
      )}
    </div>
  )
}

export default FaxDocumentPagePreview
